/**
 * 离线同步队列启动器：在 App 启动时尝试 flush 一次同步队列。
 * 真实联网检测由 worker 提供的事件总线驱动；这里只做 best-effort 启动。
 */

import { useEffect, useRef } from 'react';
import { process as processSyncQueue } from '../lib/sync/queue';
import { emitSessionResumed, recoverSession, withRecoveryTimeout } from '../api/sessionRecovery';
import { spaceApi } from '../api/space';
import type { AccountView } from '../api/types';
import { useAppDispatch, useAppState } from '../lib/state/store';

export const SyncBootstrap: React.FC = () => {
  const dispatch = useAppDispatch();
  const { currentAccount } = useAppState();
  const accountId = currentAccount?.id;
  const hydratedByResume = useRef(false);
  const applyAccount = (account: AccountView): void => {
    dispatch({ type: 'setAccount', account: {
      id: account.id,
      displayName: account.display_name,
      maskedPhone: account.masked_phone,
      avatarUrl: account.avatar_url,
    } });
  };

  const resume = async (cancelled: () => boolean): Promise<void> => {
    if (document.visibilityState === 'hidden') return;
    const account = await recoverSession();
    if (cancelled() || !account) return;
    applyAccount(account);
    const spacePromise = withRecoveryTimeout(spaceApi.getCurrent());
    // 账号恢复完成后立即广播，空间、相册、通知并行刷新。
    emitSessionResumed();
    const current = await Promise.allSettled([spacePromise]);
    if (cancelled()) return;
    const space = current[0].status === 'fulfilled' ? current[0].value : null;
    if (space) dispatch({ type: 'setSpace', space: { id: space.id, adminId: '', startDate: space.start_date, role: space.role } });
  };

  useEffect(() => {
    if (currentAccount || typeof window === 'undefined') return;
    hydratedByResume.current = true;
    let cancelled = false;
    void resume(() => cancelled).catch(() => undefined);
    return () => { cancelled = true; };
  }, [currentAccount]);

  useEffect(() => {
    if (!accountId) return;
    let cancelled = false;
    const refresh = () => {
      if (document.visibilityState === 'hidden') return;
      void resume(() => cancelled).catch(() => undefined);
    };
    if (hydratedByResume.current) {
      hydratedByResume.current = false;
    } else {
      refresh();
    }
    // Access token 默认 15 分钟；10 分钟轮询确保前台长时间停留时提前续期。
    const timer = window.setInterval(refresh, 10 * 60 * 1000);
    document.addEventListener('visibilitychange', refresh);
    window.addEventListener('pageshow', refresh);
    window.addEventListener('online', refresh);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', refresh);
      window.removeEventListener('pageshow', refresh);
      window.removeEventListener('online', refresh);
    };
  }, [accountId]);
  useEffect(() => {
    let cancelled = false;
    void processSyncQueue().catch(() => {
      // 网络层失败不影响 UI，本地保留，状态机下一次再试
    });
    const handleOnline = () => {
      if (!cancelled) {
        void processSyncQueue();
      }
    };
    window.addEventListener('online', handleOnline);
    return () => {
      cancelled = true;
      window.removeEventListener('online', handleOnline);
    };
  }, []);
  return null;
};
